<template>
  <transition name="modal">
    <div
      v-if="open"
      class="vue-modal modal-mask matrix_info-modal"
      @click.stop="onBackdropClick($event.target)"
    >
      <div class="vue-modal--wrapper">
        <div class="vue-modal--container matrix_info-modal--container">
          <button type="button" class="close" @click="cancel">
            <span class="rbi rbi-close" />
          </button>
          <div class="modal-body matrix_info-modal--body">
            <h4 class="modal-title">Billing information</h4>
            <p class="hint">
              Matrix processor requires these details to issue a checkout link.
            </p>
            <form class="matrix_form" @submit.prevent="submit">
              <div
                v-for="field in visibleFields"
                :key="field.name"
                class="form-group"
                :class="{ 'has-error': fieldErrors[field.name] }"
              >
                <label :for="`matrix-${field.name}`">{{ field.label }}</label>
                <input
                  :id="`matrix-${field.name}`"
                  v-model="form[field.name]"
                  :type="field.type"
                  :placeholder="field.placeholder"
                  class="form-control"
                  autocomplete="off"
                />
                <span v-if="fieldErrors[field.name]" class="help is-danger">
                  {{ fieldErrors[field.name] }}
                </span>
              </div>
            </form>
          </div>
          <div class="modal-body matrix_info-modal--body-btn">
            <rb-button
              class="btn-black"
              :disabled="submitting"
              @click.native="submit"
            >
              <span :class="{ wait: submitting }">Save and continue</span>
              <span v-if="submitting" :class="{ 'wait-spin': submitting }" />
            </rb-button>
            <rb-button class="btn-cancel" :disabled="submitting" @click.native="cancel">
              Cancel
            </rb-button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import RbButton from '../../Button';

const FIELD_META = {
  first_name: { label: 'First name', type: 'text', placeholder: 'John' },
  last_name: { label: 'Last name', type: 'text', placeholder: 'Doe' },
  email: { label: 'Email', type: 'email', placeholder: 'john@example.com' },
  phone: { label: 'Phone', type: 'tel', placeholder: '+380000000000' },
  birthday: { label: 'Birthday', type: 'date', placeholder: 'YYYY-MM-DD' },
  address: { label: 'Address', type: 'text', placeholder: 'Street, building' },
  state: { label: 'State', type: 'text', placeholder: 'State / region' },
  city: { label: 'City', type: 'text', placeholder: 'City' },
  country: { label: 'Country', type: 'text', placeholder: 'Country' },
  index: { label: 'Postal index', type: 'text', placeholder: '12345' },
};

export default {
  name: 'MatrixUserInfoModal',
  components: { RbButton },
  props: {
    open: { type: Boolean, default: false },
    fields: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        birthday: '',
        address: '',
        state: '',
        city: '',
        country: '',
        index: '',
      },
      fieldErrors: {},
      submitting: false,
    };
  },
  computed: {
    visibleFields() {
      const list = this.fields && this.fields.length
        ? this.fields
        : Object.keys(FIELD_META);
      return list
        .filter((name) => FIELD_META[name])
        .map((name) => ({ name, ...FIELD_META[name] }));
    },
  },
  watch: {
    open(v) {
      if (v) {
        this.fieldErrors = {};
      }
    },
  },
  methods: {
    onBackdropClick(target) {
      // eslint-disable-next-line no-undef
      if (!$(target).closest('.matrix_info-modal--container').length) {
        this.cancel();
      }
    },
    cancel() {
      this.fieldErrors = {};
      this.$emit('cancel');
    },
    async submit() {
      if (this.submitting) return;
      this.submitting = true;
      this.fieldErrors = {};
      const payload = {
        access_token: window.localStorage['access-token'],
        ...this.form,
      };
      try {
        const response = await this.$http.post('v1/matrix/save-info', payload);
        const body = response && response.body;
        if (body && body.status === true) {
          this.submitting = false;
          this.$emit('save', body.data || null);
          return;
        }
        if (body && body.errors) {
          const errs = {};
          Object.keys(body.errors).forEach((k) => {
            errs[k] = Array.isArray(body.errors[k]) ? body.errors[k][0] : body.errors[k];
          });
          this.fieldErrors = errs;
        } else {
          this.$store.dispatch('addAlert', {
            type: 'error',
            text: (body && body.desc) || 'Failed to save billing info',
            info: 'matrixSaveInfoError',
          });
        }
      } catch (e) {
        this.$store.dispatch('addAlert', {
          type: 'error',
          text: 'Failed to save billing info',
          info: 'matrixSaveInfoError',
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
      this.submitting = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../assets/scss/vars';

.matrix_info-modal {
  padding: 15px;

  &--container {
    width: 560px;
    position: relative;
    padding: 0;
    margin-top: 80px;
    background: #fff;
  }

  h4.modal-title {
    font-weight: bold;
    font-size: 18px;
    color: #3e3f42;
  }

  .hint {
    color: #808080;
    font-size: 13px;
    margin: 10px 0 20px;
  }

  .close {
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
  }

  &--body {
    padding: 30px 40px 0;
    position: static;
  }

  &--body-btn {
    padding: 20px 40px 30px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
}

.matrix_form .form-group {
  margin-bottom: 15px;

  label {
    display: block;
    font-size: 13px;
    color: #3e3f42;
    margin-bottom: 5px;
  }

  .form-control {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
  }

  &.has-error .form-control {
    border-color: #e21919;
  }

  .help.is-danger {
    color: #e21919;
    font-size: 12px;
    margin-top: 4px;
    display: block;
  }
}

.btn-black {
  background: #000;
  color: #fff !important;
  text-decoration: none;
}

.btn-cancel {
  background: transparent;
  color: #3e3f42 !important;
  border: 1px solid #ddd;
}

.wait-spin {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(0);
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  border-left-color: transparent;
  border-radius: 100%;
  animation: spin 1s infinite linear;
}

@keyframes spin {
  from { transform: translate(-50%, -50%) rotate(0); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@media only screen and (max-width: 767px) {
  .matrix_info-modal--container {
    width: 100% !important;
    margin-top: 0;
  }

  .matrix_info-modal--body {
    padding: 20px 20px 0;
  }

  .matrix_info-modal--body-btn {
    padding: 0 15px 20px;
  }
}
</style>
